import React, { useCallback, useEffect, useMemo } from 'react';
import { Box, Button, Divider, FormControl, InputLabel, MenuItem, Select, Stack, Typography } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useOperationForm } from '../../../features/patches/hooks/useOperationForm';
import { useMenuDataCache } from '../../../features/patches/hooks/useMenuDataCache';
import { OperationForm } from '../../../features/patches/components/OperationForm';
import ExclusionReconcileTable from '../../../features/patches/components/ExclusionReconcileTable';
import PendingChangesView from '../../../features/patches/components/PendingChangesView';
import { useExclusionWorkflow } from '../../../features/patches/hooks/useExclusionWorkflow';
import { entitiesFromFields, targetFromFields } from '../../../features/patches/utils/exclusionTarget';
import { isTargetComplete, missingTargetIds } from '../../../services/exclusions/exclusions.types';
import { formatOperationTypeName } from '../../../features/patches/constants/operationTypes';
import { EXCLUSION_OPERATION_TYPES } from '../../../services/exclusions/exclusions.types';

export interface StepExclusionReconcileProps {
  /**
   * The stores to search.
   *
   * Not the patches: which patch each store holds is resolved by the backend from the type suffix, because
   * the store payload's patch list is scoped to the ECOMM menu channel and misses anything assigned
   * elsewhere. An earlier cut filtered against that list here and reported "none of the 250 selected
   * stores has a Bundle Exclusion patch" for every franchise and every type.
   */
  storeNumbers: string[];
  workflow: ReturnType<typeof useExclusionWorkflow>;
}

/**
 * Reconcile step: pick one exclusion target, search the selected stores, decide per store.
 *
 * The target picker reuses the operation drawer's own form rather than growing a second set of
 * product/variant/slot/modifier selectors - those are non-trivial, menu-cache-backed components, and a
 * parallel copy would drift. `targetFromFields` reads the selection back out of the form's state.
 *
 * `mode='remove'` because that mode's forms ask only for identity, which is exactly what a target is - no
 * price fields appear.
 */
const StepExclusionReconcile: React.FC<StepExclusionReconcileProps> = ({ storeNumbers, workflow }) => {
  // The selectors are backed by the menu cache, exactly as the drawer's are.
  const { menu, fetchMenuData } = useMenuDataCache();

  const { fields, handlers } = useOperationForm({
    mode: 'remove',
    operationTypes: EXCLUSION_OPERATION_TYPES as unknown as string[],
    menu: menu || { products: [], bundles: [] },
  });

  const operationType = fields.operationType as string | null;

  // Deferred until an exclusion type is chosen. The entity selectors need the menu; the type dropdown does
  // not, and fetching on mount meant landing on this step downloaded the whole product catalogue - 45
  // requests and ~89 MB in one QA session, one response of 43 MB - before the user had chosen anything.
  // The cache is module-level, so this still happens at most once per session.
  useEffect(() => {
    if (!operationType) return;
    fetchMenuData();
  }, [operationType, fetchMenuData]);

  const target = useMemo(
    () => (operationType ? targetFromFields(operationType, fields) : null),
    [operationType, fields]
  );

  // Push the target into the workflow whenever the form changes it. The workflow clears its previous
  // answer on a new target, so a mid-search edit cannot leave decisions attached to something else.
  const targetKey = target ? JSON.stringify(target) : '';
  useEffect(() => {
    if (!target) return;
    workflow.setTarget(target, entitiesFromFields(fields));
    // targetKey, not target: the object identity changes on every render of the form.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetKey]);

  // Readiness is decided from the form, not from the workflow's copy of the target. The workflow is told
  // about the target by an effect, which lands a render later; gating the button on that round trip meant a
  // fully filled form could sit behind a disabled button. The target is also passed explicitly to the
  // search for the same reason.
  const isReady = isTargetComplete(target);

  const runSearch = useCallback(() => {
    workflow.runSearch(storeNumbers, target);
  }, [storeNumbers, target, workflow]);

  /** Why the search cannot run yet, in the user's terms. Empty when it can. */
  const blockedReason = useMemo(() => {
    if (!storeNumbers.length) return 'Select stores in step 1 first.';
    if (!operationType) return 'Choose an exclusion type.';
    if (!isReady) {
      const missing = target ? missingTargetIds(target) : [];
      const labels: Record<string, string> = {
        productId: 'a product',
        variantId: 'a variant',
        slotId: 'a slot',
        modifierId: 'a modifier',
        weightId: 'a weight',
        bundleId: 'a bundle',
      };
      const names = missing.map((field) => labels[field] ?? field);
      return names.length ? `Still to choose: ${names.join(', ')}.` : 'Complete the selection below.';
    }
    return '';
  }, [storeNumbers.length, operationType, isReady, target]);

  // Staging is what makes the decisions visible in the pending changes list, so it follows every change
  // rather than waiting for a button the user might not press before moving on.
  useEffect(() => {
    workflow.stageDecisions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workflow.counts.toAdd, workflow.counts.toRemove]);

  return (
    <Box>
      <Typography variant='h6' gutterBottom>
        Reconcile exclusions
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        Choose one thing to exclude, search the {storeNumbers.length} selected store
        {storeNumbers.length === 1 ? '' : 's'}, then decide per store.
      </Typography>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems='flex-start' sx={{ mb: 2 }}>
        <FormControl size='small' sx={{ minWidth: 260 }}>
          <InputLabel id='exclusion-operation-type-label'>Exclusion type</InputLabel>
          <Select
            labelId='exclusion-operation-type-label'
            label='Exclusion type'
            value={operationType ?? ''}
            onChange={(event) => handlers.setOperationType(event.target.value || null)}
          >
            {EXCLUSION_OPERATION_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {formatOperationTypeName(type)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant='contained'
          startIcon={<SearchIcon />}
          onClick={runSearch}
          disabled={!isReady || !storeNumbers.length || workflow.isSearching}
        >
          Search {storeNumbers.length} store{storeNumbers.length === 1 ? '' : 's'}
        </Button>
      </Stack>

      {/* Says why, rather than leaving a greyed-out button and no explanation. */}
      {blockedReason && !workflow.isSearching && (
        <Typography variant='caption' color='text.secondary' sx={{ display: 'block', mb: 2 }} data-testid='exclusion-blocked-reason'>
          {blockedReason}
        </Typography>
      )}

      {operationType && (
        <Box sx={{ mb: 2 }}>
          <OperationForm fields={fields} handlers={handlers} mode='remove' />
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      <ExclusionReconcileTable
        rows={workflow.rows}
        unreadable={workflow.unreadable}
        failed={workflow.failed}
        counts={workflow.counts}
        reach={workflow.reach}
        isSearching={workflow.isSearching}
        progress={workflow.progress}
        searchError={workflow.searchError}
        hasSearched={workflow.hasSearched}
        onDecisionChange={workflow.setDecision}
        onSelectAllMissing={workflow.selectAllMissing}
        onSelectAllFound={workflow.selectAllFound}
        onClearDecisions={workflow.clearDecisions}
      />

      {/*
        The staged operations, shown here rather than on a step of their own.
        Reusing Step3ReviewAndSubmit for that brought the whole Manage Operations surface with it - a patch
        type selector and an Add/Remove Operation drawer - which has no place in a flow where the target
        and the per-store decisions were both settled above. This is the part that was actually wanted:
        the two entries that will be submitted, and how many patches each reaches.
      */}
      {(workflow.counts.toAdd > 0 || workflow.counts.toRemove > 0) && (
        <Box sx={{ mt: 3 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant='subtitle2' gutterBottom>
            Staged for submission
          </Typography>
          <PendingChangesView open onClose={workflow.clearDecisions} />
        </Box>
      )}
    </Box>
  );
};

export default StepExclusionReconcile;
